import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  DateRangePicker,
  EmptyState,
  Input,
  ReportTable,
  type DateRangePickerValue,
  type ReportTableColumn,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import OverflowTooltipText from '../components/OverflowTooltipText';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { PermissionSlug } from '../constants/featureFlags';
import { USER_TYPE_OPTIONS, userTypeLabel } from '../constants/userTypes';
import { useDebounce } from '../hooks/useDebounce';
import { useReportsBackHref } from '../reporting/backHref';
import {
  exportUserOnboardingReportReportsUserOnboardingExportGet,
  getUserOnboardingReportReportsUserOnboardingGet,
} from '../services/api/sdk.gen';
import { SORTORDER_VALUES } from '../services/api/models/SortOrder';
import { USERONBOARDINGSORTBY_VALUES } from '../services/api/models/UserOnboardingSortBy';
import type {
  SortOrder,
  UserOnboardingReportResult,
  UserOnboardingReportRow,
  UserOnboardingSortBy,
} from '../services/api/types.gen';
import type { UserType } from '../services/api/models/UserType';
import { useStore } from '../store';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  formatDateTimeInZone,
  formatDateToISO,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  coerceReportPageSize,
  formatReportEmptyValue,
  isInvalidDateRange,
  readChoiceParam,
  readPositiveIntegerParam,
  readReportDateParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import ReportsPermissionAlert from './ReportsPermissionAlert';

const REPORT_TIMEZONE_FALLBACK = 'UTC';
const ALL_TITLES_VALUE = '__all_titles__';

function buildUserOnboardingPopulationQuery(
  includeTestRows: boolean,
  includeInactiveEpisodes: boolean
) {
  return {
    include_test_rows: includeTestRows,
    // The backend default includes inactive history; send only the active-only opt-out.
    ...(includeInactiveEpisodes ? {} : { include_inactive_episodes: false }),
  };
}

const createColumns = (
  agencyTimezone: string | null | undefined
): ReportTableColumn<UserOnboardingReportRow>[] => [
  {
    id: 'first_name',
    header: 'First Name',
    accessor: row => formatReportEmptyValue(row.first_name),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'last_name',
    header: 'Last Name',
    accessor: row => formatReportEmptyValue(row.last_name),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'email',
    header: 'Email',
    accessor: row => formatReportEmptyValue(row.email),
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'title',
    header: 'Title',
    // Reads every title held, primary first; `title` alone is the primary, and
    // is still what sorting orders on.
    accessor: row =>
      formatReportEmptyValue(
        (row.titles?.length ? row.titles : row.title ? [row.title] : [])
          .map(value => (isUserType(value) ? userTypeLabel(value) : value))
          .filter(Boolean)
          .join(', ')
      ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'phone_number',
    header: 'Phone',
    accessor: row => formatReportEmptyValue(row.phone_number),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'clinics_and_roles',
    header: 'Clinics & Roles',
    accessor: 'clinics_and_roles',
    render: value => {
      const fullValue = typeof value === 'string' ? value : null;
      return fullValue ? (
        <OverflowTooltipText
          text={fullValue}
          tooltipText={fullValue}
          className='max-w-[240px]'
        />
      ) : (
        formatReportEmptyValue(null)
      );
    },
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'invite_time',
    header: 'Invite Time',
    accessor: row =>
      formatReportEmptyValue(
        formatDateTimeInZone(
          row.invite_time,
          agencyTimezone,
          REPORT_TIMEZONE_FALLBACK,
          'M/d/yy, h:mm a'
        )
      ),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'last_login_time',
    header: 'Last Login Time',
    accessor: row =>
      formatReportEmptyValue(
        formatDateTimeInZone(
          row.last_login_time,
          agencyTimezone,
          REPORT_TIMEZONE_FALLBACK,
          'M/d/yy, h:mm a'
        )
      ),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'total_tasks_assigned',
    header: 'Total Tasks Assigned',
    accessor: 'total_tasks_assigned',
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'total_tasks_submitted',
    header: 'Total Tasks Submitted',
    accessor: 'total_tasks_submitted',
    enableSorting: true,
    minWidth: 190,
  },
];

const isUserType = (value: string | null | undefined): value is UserType =>
  USER_TYPE_OPTIONS.some(option => option.value === value);

const UserOnboardingReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsBackHref = useReportsBackHref();
  const canViewReport = useStore(state =>
    state.hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)
  );

  const sortBy: UserOnboardingSortBy = readChoiceParam(
    searchParams.get('sort'),
    USERONBOARDINGSORTBY_VALUES,
    'last_name'
  );
  const sortOrder: SortOrder = readChoiceParam(
    searchParams.get('dir'),
    SORTORDER_VALUES,
    'asc'
  );
  const page = readPositiveIntegerParam(searchParams.get('page'), 1);
  const pageSize = coerceReportPageSize(
    readPositiveIntegerParam(searchParams.get('size'), 25)
  );

  const settledQ = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(settledQ);
  const titleParam = searchParams.get('title');
  const title = isUserType(titleParam) ? titleParam : '';
  const inviteFrom = readReportDateParam(searchParams.get('invite_date_from'));
  const inviteTo = readReportDateParam(searchParams.get('invite_date_to'));
  const rawLastLoginFrom = searchParams.get('last_login_date_from');
  const rawLastLoginTo = searchParams.get('last_login_date_to');
  const neverLoggedIn = searchParams.get('never_logged_in') === 'true';
  // A deep link can contain both states. The checkbox wins until the cleanup
  // effect removes the conflicting URL dates, so no invalid request is sent.
  const lastLoginFrom = neverLoggedIn
    ? ''
    : readReportDateParam(rawLastLoginFrom);
  const lastLoginTo = neverLoggedIn ? '' : readReportDateParam(rawLastLoginTo);
  const inviteDateRange: DateRangePickerValue = {
    from: parseISOToLocalDate(inviteFrom),
    to: parseISOToLocalDate(inviteTo),
  };
  const lastLoginDateRange: DateRangePickerValue = neverLoggedIn
    ? {}
    : {
        from: parseISOToLocalDate(lastLoginFrom),
        to: parseISOToLocalDate(lastLoginTo),
      };
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') !== 'false';
  const inviteRangeInvalid = isInvalidDateRange(inviteFrom, inviteTo);
  const lastLoginRangeInvalid = isInvalidDateRange(lastLoginFrom, lastLoginTo);
  const invalidDateMessage = inviteRangeInvalid
    ? 'Invite From must be on or before Invite To.'
    : lastLoginRangeInvalid
      ? 'Last login From must be on or before Last login To.'
      : null;

  const [report, setReport] = useState<UserOnboardingReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const agencyTimezone = report?.agency_timezone ?? REPORT_TIMEZONE_FALLBACK;
  const reportColumns = useMemo(
    () => createColumns(agencyTimezone),
    [agencyTimezone]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | boolean | null>, resetPage = true) => {
      setSearchParams(current => {
        const params = new URLSearchParams(current);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === '') params.delete(key);
          else params.set(key, String(value));
        }
        if (resetPage) params.delete('page');
        return params;
      });
    },
    [setSearchParams]
  );

  const updateDateRange = useCallback(
    (fromParam: string, toParam: string, value: DateRangePickerValue) => {
      updateParams({
        [fromParam]: value.from ? formatDateToISO(value.from) : null,
        [toParam]: value.to ? formatDateToISO(value.to) : null,
      });
    },
    [updateParams]
  );

  const debouncedSetSearchParam = useDebounce(
    useCallback(
      (value: string) => updateParams({ q: value }, true),
      [updateParams]
    ),
    300
  );

  useEffect(() => {
    debouncedSetSearchParam.cancel();
    setSearchInput(settledQ);
  }, [debouncedSetSearchParam, settledQ]);

  useEffect(() => {
    if (!neverLoggedIn || (!rawLastLoginFrom && !rawLastLoginTo)) return;
    updateParams({
      last_login_date_from: null,
      last_login_date_to: null,
    });
  }, [neverLoggedIn, rawLastLoginFrom, rawLastLoginTo, updateParams]);

  const filterQuery = useMemo(
    () => ({
      q: settledQ || null,
      title: title || null,
      invite_date_from: inviteFrom || null,
      invite_date_to: inviteTo || null,
      last_login_date_from: lastLoginFrom || null,
      last_login_date_to: lastLoginTo || null,
      never_logged_in: neverLoggedIn,
      ...buildUserOnboardingPopulationQuery(
        includeTestRows,
        includeInactiveEpisodes
      ),
    }),
    [
      includeInactiveEpisodes,
      includeTestRows,
      inviteFrom,
      inviteTo,
      lastLoginFrom,
      lastLoginTo,
      neverLoggedIn,
      settledQ,
      title,
    ]
  );

  useEffect(() => {
    if (!canViewReport) return;
    if (invalidDateMessage) {
      setReport(null);
      setIsLoading(false);
      setError(invalidDateMessage);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    void getUserOnboardingReportReportsUserOnboardingGet({
      query: {
        ...filterQuery,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            getBusinessErrorMessage(response) ??
              'Unable to load user onboarding report. Please try again.'
          );
          setReport(null);
        } else setReport(response.data);
      })
      .catch(loadError => {
        console.error('Failed to load user onboarding report', loadError);
        if (!cancelled) {
          setError(
            getBusinessErrorMessage(loadError) ??
              'Unable to load user onboarding report. Please try again.'
          );
          setReport(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    canViewReport,
    filterQuery,
    invalidDateMessage,
    page,
    pageSize,
    sortBy,
    sortOrder,
  ]);

  const sorting: ReportTableSort = { id: sortBy, direction: sortOrder };
  const pagination = useMemo(
    () => ({
      page,
      pageSize,
      total: report?.total ?? 0,
      totalPages: Math.ceil((report?.total ?? 0) / pageSize),
      pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
    }),
    [page, pageSize, report?.total]
  );
  const isSearchPending = searchInput !== settledQ;

  const download = async () => {
    if (isSearchPending || invalidDateMessage) return;
    setIsExporting(true);
    try {
      const response =
        await exportUserOnboardingReportReportsUserOnboardingExportGet({
          query: {
            ...filterQuery,
            sort_by: sortBy,
            sort_order: sortOrder,
          },
        });
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to download user onboarding report. Please try again.'
          )
        );
        return;
      }
      const exportDate = formatDateTimeInZone(
        new Date().toISOString(),
        agencyTimezone,
        REPORT_TIMEZONE_FALLBACK,
        'yyyy-MM-dd'
      );
      downloadResponseAsFile(
        response as never,
        `user-onboarding-report-${exportDate}.xlsx`
      );
    } catch (downloadError) {
      console.error('Failed to download user onboarding report', downloadError);
      setError(
        await getUserFacingApiMessageAsync(
          downloadError,
          'Unable to download user onboarding report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!canViewReport) return <ReportsPermissionAlert />;
  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold'>User Onboarding Report</h1>
          <p className='text-sm text-muted-foreground'>
            Review agency members, invite times, and task activity.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download()}
            disabled={
              isLoading ||
              isExporting ||
              isSearchPending ||
              !!invalidDateMessage
            }
          >
            <Download className='h-4 w-4' /> Export
          </Button>
          <ReportPopulationControls
            description='These settings affect task totals only. They do not add or remove users.'
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? null : 'false',
              })
            }
          />
        </div>
      </div>
      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
          <label
            className='space-y-1 text-sm font-medium sm:col-span-2'
            htmlFor='user-onboarding-search'
          >
            Name or email
            <Input
              id='user-onboarding-search'
              inputSize='default'
              placeholder='Search name or email'
              value={searchInput}
              onChange={event => {
                setSearchInput(event.target.value);
                debouncedSetSearchParam(event.target.value);
              }}
            />
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='user-onboarding-title'
          >
            Title
            <Select
              value={title || ALL_TITLES_VALUE}
              onValueChange={value =>
                updateParams({
                  title: value === ALL_TITLES_VALUE ? null : value,
                })
              }
            >
              <SelectTrigger id='user-onboarding-title' selectSize='default'>
                <SelectValue placeholder='All titles' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TITLES_VALUE}>All titles</SelectItem>
                {USER_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <div className='flex items-end'>
            <label className='flex items-center gap-2 pb-2 text-sm font-medium'>
              <Checkbox
                checked={neverLoggedIn}
                onCheckedChange={checked =>
                  updateParams({
                    never_logged_in: checked ? 'true' : null,
                    last_login_date_from: checked
                      ? null
                      : lastLoginFrom || null,
                    last_login_date_to: checked ? null : lastLoginTo || null,
                  })
                }
              />
              Never logged in
            </label>
          </div>
          <DateRangePicker
            id='user-onboarding-invite-date'
            className='col-span-full w-full sm:col-span-2 lg:col-span-2'
            value={inviteDateRange}
            onChange={value =>
              updateDateRange('invite_date_from', 'invite_date_to', value)
            }
            fromLabel='Invite From'
            toLabel='Invite To'
            ariaLabel='Invite date'
            showClearButton
          />
          <DateRangePicker
            id='user-onboarding-last-login-date'
            className='col-span-full w-full sm:col-span-2 lg:col-span-2'
            value={lastLoginDateRange}
            onChange={value =>
              updateDateRange(
                'last_login_date_from',
                'last_login_date_to',
                value
              )
            }
            fromLabel='Last login From'
            toLabel='Last login To'
            ariaLabel='Last login date'
            disabled={neverLoggedIn}
            showClearButton
          />
        </div>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={reportColumns}
        getRowId={row => row.row_id}
        caption='User onboarding report results'
        sorting={sorting}
        onSortingChange={next =>
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            if (next) {
              params.set('sort', next.id);
              params.set('dir', next.direction);
            } else {
              params.delete('sort');
              params.delete('dir');
            }
            params.delete('page');
            return params;
          })
        }
        isLoading={isLoading}
        emptyState={
          <EmptyState
            title='No users found'
            description='No agency members match the selected filters.'
          />
        }
        pagination={pagination}
        onPaginationChange={next =>
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          })
        }
      />
    </div>
  );
};

export default UserOnboardingReport;
